import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, KeyRound, PhoneCall, MapPin, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Réglages généraux du portail (super_admin uniquement) :
 *  - Interrupteur ON/OFF pour l'obligation du code étudiant
 *  - Contenu du pied de page (téléphone, adresse, contact, ligne libre)
 */
export default function SiteSettings() {
  const [settings, setSettings] = useState({ require_student_code: true });
  const [footer, setFooter] = useState({
    phone: "",
    address: "",
    email: "",
    hours: "",
    extra: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings").then((r) => r.data),
      api.get("/content/site.footer").then((r) => r.data),
    ])
      .then(([s, f]) => {
        setSettings({ require_student_code: s.require_student_code ?? true });
        setFooter({ phone: "", address: "", email: "", hours: "", extra: "", ...(f || {}) });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put("/settings", settings),
        api.put("/content/site.footer", footer),
      ]);
      toast.success("Réglages enregistrés.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Chargement…</div>;

  return (
    <div data-testid="site-settings">
      <div className="eyebrow eyebrow-dark">Administration</div>
      <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Réglages du portail</h1>
      <span className="divider-gold mt-3" />

      {/* Toggle code étudiant */}
      <section className="mt-8 paper-card p-6" data-testid="section-student-code">
        <div className="flex items-start gap-4">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-unisic-gold/15 text-unisic-gold flex-shrink-0">
            <KeyRound className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-unisic-blue">
                  Obligation du code étudiant
                </h2>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed max-w-2xl">
                  Quand cet interrupteur est actif, l'étudiant doit saisir et faire valider un code
                  d'accès (généré par le Service des Dépôts) avant de pouvoir soumettre son travail.
                  Quand il est désactivé, le dépôt est ouvert directement.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-testid="toggle-require-code">
                <input
                  type="checkbox"
                  checked={settings.require_student_code}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, require_student_code: e.target.checked }))
                  }
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-slate-300 rounded-full peer peer-checked:bg-unisic-blue transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:border-slate-300 after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:after:translate-x-7 peer-checked:after:border-white" />
              </label>
            </div>
            <div className={`mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest ${settings.require_student_code ? "text-unisic-blue" : "text-slate-500"}`}>
              {settings.require_student_code ? "● Code obligatoire" : "○ Dépôt libre (sans code)"}
            </div>
          </div>
        </div>
      </section>

      {/* Pied de page */}
      <section className="mt-8 paper-card p-6" data-testid="section-footer">
        <h2 className="text-lg font-bold text-unisic-blue">Pied de page du site</h2>
        <span className="divider-gold mt-2" />
        <p className="mt-3 text-sm text-slate-600">
          Ces informations apparaissent sur toutes les pages publiques dans le pied de page.
        </p>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
              <PhoneCall className="w-3.5 h-3.5 text-unisic-gold" /> Téléphone
            </div>
            <input
              value={footer.phone}
              onChange={(e) => setFooter((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+243 xxx xxx xxx"
              className="mt-1.5 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
              data-testid="input-footer-phone"
            />
          </label>
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-unisic-gold" /> Adresse e-mail publique
            </div>
            <input
              value={footer.email}
              onChange={(e) => setFooter((f) => ({ ...f, email: e.target.value }))}
              placeholder="contact@unisic-recherche.net"
              className="mt-1.5 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
              data-testid="input-footer-email"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-unisic-gold" /> Adresse physique
            </div>
            <input
              value={footer.address}
              onChange={(e) => setFooter((f) => ({ ...f, address: e.target.value }))}
              placeholder="Campus UNISIC, Kinshasa, RDC"
              className="mt-1.5 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
              data-testid="input-footer-address"
            />
          </label>
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Horaires d'ouverture
            </div>
            <input
              value={footer.hours}
              onChange={(e) => setFooter((f) => ({ ...f, hours: e.target.value }))}
              placeholder="Lundi – vendredi, 8h30 – 17h00"
              className="mt-1.5 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
              data-testid="input-footer-hours"
            />
          </label>
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Autres informations
            </div>
            <input
              value={footer.extra}
              onChange={(e) => setFooter((f) => ({ ...f, extra: e.target.value }))}
              placeholder="Ex : compte X, Facebook, LinkedIn…"
              className="mt-1.5 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
              data-testid="input-footer-extra"
            />
          </label>
        </div>
      </section>

      <div className="mt-10 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="btn-save-settings"
        >
          <Save className="w-4 h-4" />
          {saving ? "Enregistrement..." : "Enregistrer les réglages"}
        </button>
      </div>
    </div>
  );
}
